import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MapPin, Phone, Search, ShoppingBag } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";

const CITY_LATLNG = {
  paris: [48.8566, 2.3522], lyon: [45.75, 4.85], marseille: [43.2965, 5.3698],
  toulouse: [43.6045, 1.444], lille: [50.6292, 3.0573], nantes: [47.2184, -1.5536],
  bordeaux: [44.8378, -0.5792], strasbourg: [48.5734, 7.7521], nice: [43.7102, 7.262],
  montpellier: [43.6108, 3.8767], rennes: [48.1173, -1.6778],
};
const latlngFor = (city = "") => {
  const k = Object.keys(CITY_LATLNG).find((c) => city.toLowerCase().includes(c));
  return CITY_LATLNG[k] || [46.6, 2.4];
};

export default function Directory() {
  const [pizzerias, setPizzerias] = useState([]);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => { api.get("/pizzerias").then((r) => setPizzerias(r.data)); }, []);
  const filtered = pizzerias.filter((p) => `${p.name} ${p.city}`.toLowerCase().includes(q.toLowerCase()));

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { scrollWheelZoom: false }).setView([46.6, 2.4], 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap", maxZoom: 18,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
  }, []);

  useEffect(() => {
    if (!layerRef.current || !mapRef.current) return;
    layerRef.current.clearLayers();
    const icon = L.divIcon({
      className: "",
      html: `<div style="width:38px;height:38px;border-radius:50%;background:#fff;border:3px solid #E11D48;box-shadow:0 3px 8px rgba(0,0,0,.35);overflow:hidden"><img src="/assets/logo-icon.png" style="width:100%;height:100%;object-fit:contain"/></div>`,
      iconSize: [38, 38], iconAnchor: [19, 19],
    });
    const pts = [];
    filtered.forEach((p) => {
      const ll = latlngFor(p.city);
      pts.push(ll);
      const m = L.marker(ll, { icon }).addTo(layerRef.current);
      m.bindPopup(`<b>${p.name}</b><br>${p.city}`);
      m.on("click", () => nav(`/pizzeria/${p.id}`));
    });
    if (pts.length) mapRef.current.flyToBounds(pts, { padding: [50, 50], maxZoom: 11, duration: 0.6 });
  }, [filtered, nav]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center max-w-2xl mx-auto">
        <span className="eyebrow text-cc-blue">Annuaire partenaires</span>
        <h1 className="font-display text-4xl sm:text-5xl cc-navy mt-3">TROUVEZ VOTRE PIZZERIA COCARDE</h1>
        <p className="text-slate-500 mt-3">Une carte en direct de toutes les pizzerias partenaires.</p>
      </div>
      <div className="relative max-w-md mx-auto mt-8">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ville ou nom…"
          className="w-full border border-slate-300 rounded-full pl-11 pr-4 py-3 focus:border-cc-red outline-none" />
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mt-8 items-start">
        <div ref={mapEl} data-testid="directory-map" className="w-full h-[420px] lg:h-[560px] lg:sticky lg:top-20 rounded-3xl overflow-hidden border border-slate-200 z-0" />
        <div className="space-y-4">
          {filtered.map((p) => (
            <Link key={p.id} to={`/pizzeria/${p.id}`} data-testid={`directory-card-${p.id}`} className="card-cocarde overflow-hidden flex">
              <img src={p.image_url} alt={p.name} className="w-28 sm:w-32 object-cover shrink-0" />
              <div className="p-4 flex-1">
                <h3 className="font-display text-xl cc-navy">{p.name}</h3>
                <p className="text-slate-500 text-sm flex items-center gap-1 mt-1"><MapPin size={14} /> {p.address}</p>
                {p.phone && <p className="text-slate-500 text-sm flex items-center gap-1"><Phone size={14} /> {p.phone}</p>}
                <p className="text-slate-600 text-sm mt-2 line-clamp-2">{p.description}</p>
                <span className="btn-cocarde mt-3 text-sm inline-flex items-center gap-2 py-2 px-4"><ShoppingBag size={15} /> Voir & commander</span>
              </div>
            </Link>
          ))}
          {filtered.length === 0 && <p className="text-center text-slate-400 mt-10">Aucune pizzeria trouvée.</p>}
        </div>
      </div>
    </div>
  );
}
